// AI-generated thread summary card for the ticket detail page.
//
// Lazy-loaded: doesn't fire the Edge Function until the user actually
// clicks the Summary tab. The hook key includes the latest message id,
// so a new client reply (or team outbound) auto-invalidates the cached
// summary on next view.
//
// Rendering: a small structured card with 5 sections (TL;DR, what the
// client wants, what we've tried, open questions, recommended next
// step). Empty sections are hidden so trivial threads don't show empty
// rows.

import { useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  HelpCircle,
  Sparkles,
  Target,
  Wrench,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useThreadSummary, type ThreadSummary as Summary } from '@/lib/queries';

interface Props {
  ticketId: string;
  /** ID of the most recent message — gates the cache key so new replies
   *  bust the prior summary automatically. */
  latestMessageId: string | undefined;
  /** Total message count. Used to short-circuit the UI for trivial threads
   *  ("just one inbound") with a friendlier empty state. */
  messageCount: number;
}

export function ThreadSummary({ ticketId, latestMessageId, messageCount }: Props) {
  // We don't auto-fire on tab mount — the user clicks "Summarise" once.
  // Subsequent renders within the session use the cached value (5min
  // staleTime + the latest-message-id key).
  const [hasRequested, setHasRequested] = useState(false);
  const q = useThreadSummary(ticketId, latestMessageId, hasRequested);

  if (messageCount === 0) {
    return (
      <p className="px-4 py-12 text-center text-sm text-muted-foreground">
        No messages yet — nothing to summarise.
      </p>
    );
  }

  if (messageCount === 1 && !hasRequested) {
    // One inbound, no replies — generating a summary is overkill. Show a
    // soft hint but still let them generate one if they really want.
    return (
      <div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
        <Sparkles className="h-5 w-5 text-muted-foreground" />
        <div>
          <p className="text-sm font-medium">Just one message so far</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Read the original above — a summary won't tell you anything new yet.
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => setHasRequested(true)}>
          <Sparkles className="mr-1 h-3.5 w-3.5" /> Summarise anyway
        </Button>
      </div>
    );
  }

  if (!hasRequested) {
    return (
      <div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
        <Sparkles className="h-5 w-5 text-muted-foreground" />
        <div>
          <p className="text-sm font-medium">Catch up on this thread</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Generate a structured summary of all {messageCount} messages.
            Uses Claude Haiku (~$0.001 per summary).
          </p>
        </div>
        <Button size="sm" onClick={() => setHasRequested(true)}>
          <Sparkles className="mr-1 h-3.5 w-3.5" /> Summarise thread
        </Button>
      </div>
    );
  }

  if (q.isLoading) {
    return (
      <div className="flex flex-col gap-3 px-4 py-4">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-12 w-full" />
      </div>
    );
  }

  if (q.error) {
    return (
      <div className="flex flex-col items-center gap-3 px-4 py-10 text-center">
        <AlertCircle className="h-5 w-5 text-tint-critical-fg" />
        <div>
          <p className="text-sm font-medium text-tint-critical-fg">Couldn't generate summary</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {q.error instanceof Error ? q.error.message : String(q.error)}
          </p>
        </div>
        <Button size="sm" variant="outline" onClick={() => q.refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  if (!q.data) return null;

  return <SummaryCard summary={q.data} onRefresh={() => q.refetch()} refreshing={q.isFetching} />;
}

function SummaryCard({
  summary,
  onRefresh,
  refreshing,
}: {
  summary: Summary;
  onRefresh: () => void;
  refreshing: boolean;
}) {
  return (
    <div className="flex flex-col gap-3 px-4 py-4">
      <div className="rounded-lg border border-border/70 bg-muted/30 px-4 py-3">
        <p className="text-3xs font-semibold uppercase tracking-wide text-muted-foreground">
          TL;DR
        </p>
        <p className="mt-1 text-sm leading-relaxed text-foreground">{summary.tldr}</p>
      </div>

      <Section icon={Target} label="What the client wants" body={summary.client_intent} />
      <Section icon={Wrench} label="What we've tried" body={summary.what_we_tried} />
      <Section icon={HelpCircle} label="Open questions" body={summary.open_questions} />
      <Section
        icon={CheckCircle2}
        label="Recommended next step"
        body={summary.next_step}
        accent="emerald"
      />

      <div className="flex items-center justify-between border-t border-border/40 pt-2 text-3xs text-muted-foreground">
        <span>Generated by Claude Haiku · regenerates when a new message arrives</span>
        <button
          type="button"
          onClick={onRefresh}
          disabled={refreshing}
          className="rounded px-2 py-0.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        >
          {refreshing ? 'Refreshing…' : 'Regenerate'}
        </button>
      </div>
    </div>
  );
}

function Section({
  icon: Icon,
  label,
  body,
  accent,
}: {
  icon: typeof Sparkles;
  label: string;
  body: string;
  accent?: 'emerald';
}) {
  if (!body || body.trim().length === 0) return null;
  return (
    <div className="flex flex-col gap-1 rounded-md border border-border/60 bg-background px-4 py-3">
      <p
        className={
          'flex items-center gap-1.5 text-3xs font-semibold uppercase tracking-wide ' +
          (accent === 'emerald' ? 'text-tint-success-fg' : 'text-muted-foreground')
        }
      >
        <Icon className="h-3 w-3" />
        {label}
      </p>
      <p className="text-sm leading-relaxed text-foreground/90">{body}</p>
    </div>
  );
}
